import { AppBar, Toolbar, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import WebFont from 'webfontloader';
import Logo from '../icons/Logo';
import { SearchProvider } from '../../contexts/SearchContext';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import RegisterButton from '../atoms/RegisterButton';
import SignInButton from '../atoms/SignInButton';
import ProfileButton from '../atoms/ProfileButton';
import BalanceButton from '../atoms/BalanceButton';
import TranslateButton from '../atoms/TranslateButton';
import { mergeSx } from '../../utils/styles';

type BrandConfig = Record<
  string,
  {
    text: string;
    styles?: SxProps<Theme>;
    font?: {
      family: string;
      weights?: string;
    };
  }
>;

export const CASINO_HEADER_HEIGHT = { xs: 54, md: 70 };

const styles = {
  appBar: {
    height: { xs: CASINO_HEADER_HEIGHT.xs, md: CASINO_HEADER_HEIGHT.md },
    backgroundColor: 'background.default',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    zIndex: 1000,
    boxShadow: 'none',
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
  },
  toolbar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: { xs: 1, md: 3 },
  },
  brandName: {
    color: 'primary.main',
    fontWeight: 700,
    fontSize: { xs: 16, md: 26 },
    lineHeight: 1.1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minWidth: 0,
    flexShrink: 1,
  },
  leftStack: {
    minWidth: 0,
    flexShrink: 1,
    overflow: 'hidden',
  },
  authButton: {
    maxWidth: { xs: 100, md: 'none' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    minWidth: 0,
    typography: { xs: 'body4', md: 'body2' },
    fontWeight: { xs: 600, md: 600 },
  },
};

const CasinoHeader = () => {
  const { isAuthenticated } = useAuth();
  const brandConfig = useFeatureValue<BrandConfig>('casino-header-branding', {});

  const brandSettings = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return brandConfig?.[window.location.hostname] ?? null;
  }, [brandConfig]);

  const fontFamily = useMemo(() => {
    if (!brandSettings?.font?.family) {
      return undefined;
    }

    return { fontFamily: `${brandSettings.font.family}, sans-serif` };
  }, [brandSettings?.font?.family]);

  const brandTypography = useMemo(
    () => mergeSx(styles.brandName, { ...brandSettings?.styles, ...fontFamily }),
    [brandSettings?.styles, fontFamily]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!brandSettings?.font) {
      return;
    }

    const { family, weights = '400,500,700' } = brandSettings.font;

    WebFont.load({
      google: {
        families: [`${family}:${weights}`],
      },
    });
  }, [brandSettings]);

  return (
    <SearchProvider>
      <AppBar sx={styles.appBar}>
        <Toolbar sx={styles.toolbar}>
          <Stack
            direction='row'
            spacing={1}
            sx={mergeSx(
              {
                alignItems: 'center',
              },
              styles.leftStack
            )}
          >
            <Logo size='small' />
            {brandSettings?.text && (
              <Typography component='span' sx={brandTypography}>
                {brandSettings.text}
              </Typography>
            )}
          </Stack>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <TranslateButton />
            {isAuthenticated ? (
              <>
                <BalanceButton />
                <ProfileButton />
              </>
            ) : (
              <>
                <SignInButton sx={styles.authButton} />
                <RegisterButton sx={styles.authButton} />
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
    </SearchProvider>
  );
};

export default CasinoHeader;
